/**
 * uploadDirectToDrive — CHK-4 client-side helper.
 *
 * Three-step browser→Drive upload that bypasses Render's body cap:
 *   1. POST /api/uploads/initiate to get a Drive resumable upload URL.
 *   2. PUT the file body directly to that URL (with progress callback).
 *   3. POST /api/uploads/finalize so the backend records the file in our DB.
 *
 * Use this instead of the legacy base64-in-JSON / multipart-FormData paths
 * for any file that may exceed ~25-32 MB (i.e. the application uploads,
 * client-portal uploads, deal-preview uploads, introducer-document uploads).
 *
 * Each `target` shape mirrors what the /uploads endpoints accept:
 *   - { target_kind: "application_doc_request", application_id, doc_request_id?, category? }
 *   - { target_kind: "application_general", application_id, category? }
 *   - { target_kind: "introducer_document", introducer_id, category? }
 *
 * onProgress receives a number between 0 and 1.
 */
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const MAX_DIRECT_UPLOAD_BYTES = 250 * 1024 * 1024;

export class DirectUploadError extends Error {
  constructor(stage, message, original) {
    super(message);
    this.name = "DirectUploadError";
    this.stage = stage; // 'initiate' | 'put' | 'finalize'
    this.original = original;
  }
}

/**
 * @param {File|Blob} file
 * @param {object} target
 * @param {{ onProgress?: (fraction:number)=>void, fileName?: string }} [opts]
 * @returns {Promise<{document: object, drive_file_id: string}>}
 */
export async function uploadDirectToDrive(file, target, opts = {}) {
  if (!file) throw new DirectUploadError("initiate", "No file provided");
  const fileName = opts.fileName || file.name || "upload.bin";
  const mimeType = file.type || "application/octet-stream";
  const fileSize = file.size;
  if (fileSize > MAX_DIRECT_UPLOAD_BYTES) {
    const mb = Math.round(MAX_DIRECT_UPLOAD_BYTES / (1024 * 1024));
    throw new DirectUploadError("initiate", `File is too large — max ${mb} MB`);
  }

  // ---- Step 1: initiate ----
  const initiateBody = {
    ...target,
    file_name: fileName,
    mime_type: mimeType,
    file_size: fileSize,
  };
  let initRes;
  try {
    initRes = await axios.post(`${API}/uploads/initiate`, initiateBody);
  } catch (err) {
    throw new DirectUploadError("initiate", err?.response?.data?.detail || "Could not start upload", err);
  }
  const uploadUrl = initRes.data?.upload_url;
  if (!uploadUrl) {
    throw new DirectUploadError("initiate", "Server did not return an upload URL");
  }

  // ---- Step 2: PUT file directly to Drive ----
  // Use a fresh axios instance so our globally-set Authorization Bearer
  // token isn't forwarded to googleapis.com (which would either fail CORS
  // preflight or be rejected by Drive).
  let driveFileId = null;
  try {
    const driveAxios = axios.create();
    const putRes = await driveAxios.put(uploadUrl, file, {
      headers: {
        "Content-Type": mimeType,
      },
      transformRequest: [(data) => data],
      onUploadProgress: (e) => {
        if (typeof opts.onProgress === "function" && e.total) {
          opts.onProgress(e.loaded / e.total);
        }
      },
    });
    // Drive returns 200/201 with a JSON body containing the file's id.
    driveFileId = putRes.data?.id;
    if (!driveFileId && typeof putRes.data === "string") {
      try { driveFileId = JSON.parse(putRes.data).id; } catch (_) { /* ignore */ }
    }
  } catch (err) {
    throw new DirectUploadError("put", err?.message || "Upload to Drive failed", err);
  }
  if (!driveFileId) {
    throw new DirectUploadError("put", "Drive did not return a file id");
  }

  // ---- Step 3: finalize ----
  try {
    const finRes = await axios.post(`${API}/uploads/finalize`, {
      ...target,
      drive_file_id: driveFileId,
      file_name: fileName,
      mime_type: mimeType,
      file_size: fileSize,
    });
    return { ...finRes.data, drive_file_id: driveFileId };
  } catch (err) {
    throw new DirectUploadError("finalize", err?.response?.data?.detail || "Could not record uploaded file", err);
  }
}
